import { useAuth0 } from '@auth0/auth0-react';
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useDebounceValue } from '@/hooks';

// ES 2015
import { EnergyUsageByHour } from './EnergyUsageByHour';
import { EnergyUsageByHourInput } from './EnergyUsageByHourInput';
import { EnergyUsageByHourResponse } from './EnergyUsageByHourResponse';

const { VITE_GREENSEE_API_URL, VITE_AUTH0_AUDIENCE } = import.meta.env;

export function usePostEnergyUsageByHour(
  params: EnergyUsageByHourInput,
): [UseQueryResult<EnergyUsageByHour[], Error>, () => void] {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const body = JSON.stringify(params);
  const debouncedKey = useDebounceValue(body);
  const queryKey = ['greensee-api-post-energy-usage-by-hour', debouncedKey];

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      const response = await fetch(
        `${VITE_GREENSEE_API_URL}/energy-usage-by-hour`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.access_token}`,
          },
          body,
        },
      );

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(
          `Error while fetching energy usage: Invalid Content-Type response ${response.status}`,
        );
      }

      const result: EnergyUsageByHourResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          `Error while fetching energy usage: ${response.status} ${result.message || ''}`,
        );
      }

      const dataset = result.data || [];
      return dataset;
    },
  });

  return [result, () => queryClient.invalidateQueries({ queryKey })];
}
